import React, { useEffect, useMemo, useState } from 'react';
import brandBanner from 'assets/images/batched-order-brand-banner-placeholder.png';
import brandLogo from 'assets/images/batched-order-brand-logo-placeholder.png';
import { ReactComponent as TimeIcon } from 'assets/icons/time.svg';
import { ReactComponent as BasketIcon } from 'assets/icons/basket-icon.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { BrandType } from 'models/brand.model';
import { convertTimeRange, formatTimeToCompactFormat } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import moment from 'moment';
import OrderConfirmationTimelineModal from 'components/modals/order-confirmation-timeline-modal';
import useAxios from 'hooks/useAxios';
import { BatchOrderCountType, MinimumOrderType } from 'models/order.model';
import { ENUM_BRAND_TYPE, FCB_BATCH_ORDER_STATUS } from 'utils/enum';

const mealMapping = {
  fcb_custom_breakfast: 'Breakfast',
  fcb_custom_lunch: 'Lunch',
  fcb_custom_afterhours: 'Take home'
};

const BatchedOrderBrandCard = ({
  brand,
  handleClick,
  day
}: {
  brand: BrandType;
  handleClick: () => void;
  day: string;
}) => {
  const [isOrderInBasket] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const { activeCompany } = useDashboardContext();

  const { fetchData, response, loading } = useAxios<{ data: { data: MinimumOrderType } }>({
    url: `company/${activeCompany?.id}/minimum-orders`
  });
  const {
    fetchData: fetchBatchOrderDetails,
    response: res,
    loading: isLoading
  } = useAxios<{ data: { data: BatchOrderCountType } }>({
    baseURL: 'https://order-api.getfoodcourt.com/v1/',
    url: `batch-orders/count/orders?company_id=${activeCompany?.id}&brand_id=${brand?.id}`
  });
  const orderCountRes = useMemo(() => res?.data?.data ?? ({} as BatchOrderCountType), [res]);
  const isPreorder = brand.brand_type === ENUM_BRAND_TYPE.FOODCOURT_BUSINESS;

  const timeCheck = (timeString: string) => {
    const now = moment();
    const time = moment(timeString);
    const differenceInMinutes = time.diff(now, 'minutes');
    return differenceInMinutes >= 0 && differenceInMinutes <= 60;
  };
  const isWithinOneHour = timeCheck(moment(brand.cutoff_time, 'HH:mm:ss').toString());

  useEffect(() => {
    fetchBatchOrderDetails();
    fetchData();
  }, []);

  const handleOrderConfirmationTimeline = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setShowTimeline(true);
  };

  return (
    <>
      {brand.delivery_time || isPreorder ? (
        <div className='cursor-pointer' onClick={handleClick}>
          <div className='rounded-lg bg-white shadow-[0px_3px_10px_0px_rgba(100,_102,_104,_0.10)] overflow-hidden'>
            <div className='relative'>
              <img
                src={brand.images[0] || brandBanner}
                alt='brand-banner'
                className='w-full aspect-video object-cover'
              />
              {isPreorder ? (
                <div className='absolute top-3 left-3 text-gray-10 py-1 px-2 bg-white border-2 shadow-[0px_2px_10px_0px_#66666633] rounded-lg flex items-center gap-1.5 border-yellow-5'>
                  <TimeIcon className='w-4 h-[14px] stroke-gray-5 fill-gray-5' />
                  <p>Pre-order</p>
                </div>
              ) : (
                <div
                  onClick={handleOrderConfirmationTimeline}
                  className={`absolute top-3 left-3 text-gray-10 py-1 px-2 bg-white border-2  ${
                    isWithinOneHour ? 'border-red-5 animate-bounce' : 'border-green-5'
                  } shadow-[0px_2px_10px_0px_#66666633] rounded-lg flex items-center gap-1.5`}>
                  <TimeIcon className='w-4 h-[14px] stroke-gray-5 fill-gray-5' />
                  <div className='flex flex-col text-[11px]'>
                    {brand.delivery_time && (
                      <span>
                        <span className='font-medium mr-1 text-xs'>
                          {mealMapping?.[brand?.brand_type as keyof typeof mealMapping]}:
                        </span>
                        {convertTimeRange(brand.delivery_time)}
                      </span>
                    )}
                    <span>
                      Order before {formatTimeToCompactFormat(brand.cutoff_time)} {day}
                    </span>
                  </div>
                </div>
              )}
              {isOrderInBasket && (
                <div className='absolute bottom-2 right-2 bg-red-5 rounded-[100px] py-1 pr-2 pl-1 flex items-center gap-1'>
                  <div className='p-[3.5px] bg-white rounded-full'>
                    <BasketIcon className='w-3 h-3 text-red-5' />
                  </div>
                  <span className='text-white'>Basket</span>
                </div>
              )}
              {(orderCountRes?.status === FCB_BATCH_ORDER_STATUS.ONGOING ||
                orderCountRes?.status === FCB_BATCH_ORDER_STATUS.COMPLETED) && (
                <div className='absolute bottom-2 right-2 bg-green-8 rounded-[100px] py-1 pr-2 pl-1 flex items-center gap-1 shadow-[0px_4px_30px_0px_#6666664D]'>
                  <div className='p-0.5 bg-white rounded-full'>
                    <CheckIcon className='w-3 h-3 text-green-8' />
                  </div>
                  <span className='text-white text-xs font-medium'>Ordered</span>
                </div>
              )}
            </div>
            <div className='w-full flex items-start gap-3 p-4'>
              <img
                src={brand.logo || brandLogo}
                alt='brand-logo'
                className='w-[42px] h-[42px] rounded-full'
              />
              <article>
                <h6 className='font-medium'>{brand.name}</h6>
                <p className='text-gray-9 text-sm line-clamp-1'>{brand.description}</p>
              </article>
            </div>
          </div>
        </div>
      ) : (
        <div className='rounded-lg bg-white shadow-[0px_3px_10px_0px_rgba(100,_102,_104,_0.10)] overflow-hidden cursor-not-allowed'>
          <div className='relative'>
            <img
              src={brand.images[0] || brandBanner}
              alt='brand-banner'
              className='w-full aspect-video object-cover brightness-[30%]'
            />
            <div
              className={`absolute top-3 left-3 text-gray-10 py-1 px-2 bg-white border-2  ${
                isWithinOneHour ? 'border-red-5 animate-bounce' : 'border-green-5'
              } shadow-[0px_2px_10px_0px_#66666633] rounded-lg flex items-center gap-1.5`}>
              <TimeIcon className='w-4 h-[14px] stroke-gray-5 fill-gray-5' />
              <div className='flex flex-col text-[11px]'>
                {
                  <span>
                    <span className='font-medium mr-1 text-xs'>
                      {mealMapping?.[brand?.brand_type as keyof typeof mealMapping]}:
                    </span>
                  </span>
                }
                <span>
                  Order before {formatTimeToCompactFormat(brand.cutoff_time)} {day}
                </span>
              </div>
            </div>
            {isOrderInBasket && (
              <div className='absolute bottom-2 right-2 bg-red-5 rounded-[100px] py-1 pr-2 pl-1 flex items-center gap-1'>
                <div className='p-[3.5px] bg-white rounded-full'>
                  <BasketIcon className='w-3 h-3 text-red-5' />
                </div>
                <span className='text-white'>Basket</span>
              </div>
            )}
            {(orderCountRes?.status === FCB_BATCH_ORDER_STATUS.ONGOING ||
              orderCountRes?.status === FCB_BATCH_ORDER_STATUS.COMPLETED) && (
              <div className='absolute bottom-2 right-2 bg-green-8 rounded-[100px] py-1 pr-2 pl-1 flex items-center gap-1 shadow-[0px_4px_30px_0px_#6666664D]'>
                <div className='p-0.5 bg-white rounded-full'>
                  <CheckIcon className='w-3 h-3 text-green-8' />
                </div>
                <span className='text-white font-medium text-xs'>Ordered</span>
              </div>
            )}
          </div>
          <div className='w-full flex items-start gap-3 p-4'>
            <img
              src={brand.logo || brandLogo}
              alt='brand-logo'
              className='w-[42px] h-[42px] rounded-full'
            />
            <article>
              <h6 className='font-medium'>{brand.name}</h6>
              <p className='text-gray-9 text-sm line-clamp-1'>{brand.description}</p>
            </article>
          </div>
        </div>
      )}
      {showTimeline && (
        <OrderConfirmationTimelineModal
          day={day}
          isOpen={showTimeline}
          onClose={() => setShowTimeline(false)}
          cutoffTime={brand?.cutoff_time}
          isWithingOneHour={isWithinOneHour}
          isMinOrderLoading={loading}
          isOrderCountLoading={isLoading}
          minOrderRes={response?.data?.data}
          orderCountRes={orderCountRes}
        />
      )}
    </>
  );
};

export default BatchedOrderBrandCard;
